/**
 * Cuisine Filter Service using ChatGPT API
 * Filters cuisine foods based on user scan results
 */

import { ScanAnalysis } from '../services/scanService';

interface CuisineFoodRecommendation {
  name: string;
  description: string;
  type: 'meal' | 'snack';
  recipe?: {
    ingredients: string[];
    instructions: string[];
    caloriesPerServing: number;
    servings: number;
  };
  skinBenefits: string;
  cuisine: string;
}

export function getProxyUrl(): string {
  const raw = process.env.REACT_APP_API_URL;
  if (typeof raw !== 'string' || !raw.trim().startsWith('http')) {
    throw new Error('REACT_APP_API_URL is required. Set it to your Lambda/API base URL.');
  }
  return raw.trim().replace(/\/+$/, '');
}

function buildCuisinePrompts(
  cuisine: string,
  scanAnalysis: ScanAnalysis,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian',
) {
  const skinType = scanAnalysis.acneAnalysis?.estimatedPH
    ? scanAnalysis.acneAnalysis.estimatedPH < 4.7
      ? 'sensitive'
      : scanAnalysis.acneAnalysis.estimatedPH > 5.9
        ? 'dry'
        : 'normal'
    : 'normal';

  const acneSeverity =
    scanAnalysis.acneAnalysis?.overallSeverity || scanAnalysis.acneSeverity || 'none';

  const dietaryPreferenceText =
    dietaryPreference === 'vegetarian'
      ? 'vegetarian only'
      : dietaryPreference === 'non-vegetarian'
        ? 'mix vegetarian and non-vegetarian'
        : 'any diet';

  // Compact prompt + JSON mode keeps responses under API Gateway's ~29s limit.
  const systemPrompt = `You are a nutrition expert. Return ONLY valid JSON (no markdown) with this shape:
{"items":[{"name":"","description":"","type":"meal"|"snack","recipe":{"ingredients":[""],"instructions":[""],"caloriesPerServing":number,"servings":number},"skinBenefits":"","cuisine":"${cuisine}"}]}
Rules:
- Exactly 3 authentic ${cuisine} meals (type "meal") and 2 snacks (type "snack").
- Acne-friendly: anti-inflammatory, low sugar, avoid heavy dairy/fried food.
- Real dish names (e.g. Dal, Biryani, Ramen — match ${cuisine}).
- Max 5 ingredients and 3 short instruction steps per recipe.
- Unique caloriesPerServing per item (snacks 90-180, meals 260-480).
- Diet: ${dietaryPreferenceText}.`;

  const userMessage = `Skin: ${skinType}. Acne: ${acneSeverity}. ${dietaryPreferenceText}. ${cuisine} cuisine.`;

  return { systemPrompt, userMessage };
}

function parseRecommendationsFromContent(content: string): CuisineFoodRecommendation[] {
  let jsonContent = content.trim();
  if (jsonContent.includes('```json')) {
    jsonContent = jsonContent.split('```json')[1].split('```')[0].trim();
  } else if (jsonContent.includes('```')) {
    jsonContent = jsonContent.split('```')[1].split('```')[0].trim();
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(jsonContent);
  } catch (parseErr) {
    if (parseErr instanceof SyntaxError) {
      const lastObj = jsonContent.lastIndexOf('},');
      if (lastObj !== -1) {
        parsed = JSON.parse(`${jsonContent.substring(0, lastObj + 1)}]`);
      } else {
        throw new Error('The response was incomplete. Please try again.');
      }
    } else {
      throw parseErr;
    }
  }

  if (Array.isArray(parsed)) {
    return parsed as CuisineFoodRecommendation[];
  }
  if (parsed && typeof parsed === 'object') {
    const obj = parsed as Record<string, unknown>;
    const items = obj.items ?? obj.recommendations ?? obj.foods;
    if (Array.isArray(items)) {
      return items as CuisineFoodRecommendation[];
    }
  }

  throw new Error('Invalid response format. Please try again.');
}

function normalizeRecommendations(
  recommendations: CuisineFoodRecommendation[],
): CuisineFoodRecommendation[] {
  const normalized = recommendations
    .filter((rec) => rec && rec.name && rec.description && rec.cuisine && rec.recipe)
    .map((rec) => {
      const r = { ...rec };
      const rawType = String((r as { type?: string }).type || '').toLowerCase();
      (r as { type: 'meal' | 'snack' }).type = rawType.includes('snack') ? 'snack' : 'meal';
      if (r.recipe) {
        r.recipe = { ...r.recipe };
        if (!Array.isArray(r.recipe.ingredients)) {
          const ing = (r.recipe as { ingredients?: unknown }).ingredients;
          r.recipe.ingredients =
            typeof ing === 'string'
              ? [ing]
              : Array.isArray(ing)
                ? ing.filter((x: unknown) => typeof x === 'string')
                : [];
        }
        let instr =
          (r.recipe as { instructions?: unknown; steps?: unknown }).instructions ??
          (r.recipe as { steps?: unknown }).steps;
        if (!Array.isArray(instr)) {
          r.recipe.instructions =
            typeof instr === 'string'
              ? (instr as string).split(/\n+/).map((s: string) => s.trim()).filter(Boolean)
              : [];
        } else {
          r.recipe.instructions = (instr as string[]).map((s: unknown) => String(s)).filter(Boolean);
        }
        if (r.recipe.instructions.length === 0) {
          r.recipe.instructions = ['Combine ingredients and cook as desired.'];
        }
        if (typeof r.recipe.caloriesPerServing !== 'number') {
          r.recipe.caloriesPerServing = (r.recipe.caloriesPerServing as number) ?? 300;
        }
        if (typeof r.recipe.servings !== 'number') {
          r.recipe.servings = (r.recipe.servings as number) ?? 2;
        }
      }
      return r;
    })
    .filter((rec) => rec.recipe && rec.recipe.ingredients.length > 0);

  const cals = normalized.map((r) => r.recipe?.caloriesPerServing);
  const allSame = cals.length > 1 && cals.every((c) => c === cals[0]);
  if (allSame && normalized.length > 0) {
    let mealIdx = 0;
    let snackIdx = 0;
    normalized.forEach((rec) => {
      if (rec.recipe) {
        if (rec.type === 'snack') {
          rec.recipe.caloriesPerServing = 110 + snackIdx * 35;
          snackIdx++;
        } else {
          rec.recipe.caloriesPerServing = 280 + mealIdx * 55;
          mealIdx++;
        }
      }
    });
  }

  if (normalized.length === 0) {
    throw new Error("We couldn't load recommendations right now. Please try again.");
  }

  if (normalized.filter((r) => r.type === 'snack').length === 0 && normalized.length > 3) {
    const sorted = [...normalized].sort(
      (a, b) => (a.recipe?.caloriesPerServing ?? 400) - (b.recipe?.caloriesPerServing ?? 400),
    );
    const toSnacks = Math.min(2, sorted.length - 3);
    for (let i = 0; i < toSnacks && i < sorted.length; i++) {
      const item = sorted[i];
      if (item && item.type === 'meal') {
        (item as { type: 'meal' | 'snack' }).type = 'snack';
      }
    }
  }

  return normalized;
}

async function callOpenAIChat(
  backendUrl: string,
  systemPrompt: string,
  userMessage: string,
  maxTokens: number,
): Promise<string> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 45000);

  try {
    const response = await fetch(`${backendUrl}/api/openai-chat`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        model: 'gpt-4o-mini',
        messages: [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userMessage },
        ],
        temperature: 0.5,
        max_tokens: maxTokens,
        response_format: { type: 'json_object' },
      }),
      signal: controller.signal,
    });
    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = (await response.json().catch(() => ({}))) as {
        error?: string | { message?: string };
        message?: string;
      };
      const msg =
        typeof errorData?.error === 'string'
          ? errorData.error
          : (errorData?.error as { message?: string })?.message ??
            errorData?.message ??
            `OpenAI API error: ${response.status} ${response.statusText}`;

      if (
        response.status === 504 ||
        (typeof msg === 'string' && /timed out|timeout|too long/i.test(msg))
      ) {
        throw new Error('TIMEOUT');
      }
      if (
        response.status === 402 ||
        (typeof msg === 'string' && /quota|billing|insufficient/i.test(msg))
      ) {
        throw new Error(
          'OpenAI billing or quota issue. Add a payment method at platform.openai.com/account/billing and retry.',
        );
      }
      throw new Error(msg);
    }

    const data = await response.json();
    if (!data.choices || data.choices.length === 0) {
      throw new Error('No response from OpenAI API');
    }
    return data.choices[0].message.content.trim();
  } catch (err) {
    clearTimeout(timeoutId);
    throw err;
  }
}

export const filterCuisineFoodsByScan = async (
  cuisine: string,
  scanAnalysis: ScanAnalysis | null,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian',
): Promise<CuisineFoodRecommendation[]> => {
  const backendUrl = getProxyUrl();

  if (!scanAnalysis) {
    throw new Error('Scan analysis is required to filter cuisine foods.');
  }

  const { systemPrompt, userMessage } = buildCuisinePrompts(
    cuisine,
    scanAnalysis,
    dietaryPreference,
  );

  try {
    let content: string;
    try {
      content = await callOpenAIChat(backendUrl, systemPrompt, userMessage, 900);
    } catch (firstErr) {
      if (firstErr instanceof Error && firstErr.message === 'TIMEOUT') {
        // One lighter retry (2 meals + 2 snacks) if the first call hit API Gateway limit.
        const retrySystem = systemPrompt.replace(
          'Exactly 3 authentic',
          'Exactly 2 authentic',
        ).replace('and 2 snacks', 'and 2 snacks');
        content = await callOpenAIChat(backendUrl, retrySystem, userMessage, 650);
      } else {
        throw firstErr;
      }
    }

    const recommendations = parseRecommendationsFromContent(content);
    return normalizeRecommendations(recommendations);
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : String(err);
    console.error('[Cuisine Filter] Request failed:', msg, err);
    if (err instanceof Error) {
      if (err.name === 'AbortError' || err.message === 'TIMEOUT') {
        throw new Error(
          'Recommendations are taking longer than usual. Try a shorter cuisine name or try again in a moment.',
        );
      }
      if (/fetch|network|failed to fetch|cors|typeerror/i.test(msg)) {
        throw new Error(
          'Network or CORS error. Ensure REACT_APP_API_URL points to your Lambda/API and the backend is deployed.',
        );
      }
    }
    throw err;
  }
};
